import clsx from "clsx";
import { useRef, useState, useEffect, memo, useMemo } from "react";
import PhoneValidation, {
	PhoneValidationData,
} from "@/components/consultationForm/parts/phoneValidation";
import { stateOption } from "@/lib/constants";
import { EnvelopeIcon } from "@phosphor-icons/react";
import { FormHeader, ToggleSwitch } from "@/components/ui";
import { secureInput } from "@/lib/security";
import { useFormStateStore } from "@/components/context/useFormContext";

export interface DetailsFormData {
	firstName: string;
	lastName: string;
	email: string;
	phone: PhoneValidationData["phone"];
	phoneVerified: PhoneValidationData["phoneVerified"];
	phoneStage: PhoneValidationData["phoneStage"];
	state: string;
	postcode: string;
	tc: boolean;
	message: string;
}

export interface DetailsFormErrors {
	firstName: boolean;
	lastName: boolean;
	email: boolean;
	phone: boolean;
	phoneVerified: boolean;
	state: boolean;
	postcode: boolean;
	tc: boolean;
	message: boolean;
}

interface FormDataProps {
	formData: DetailsFormData;
	setFormData: React.Dispatch<React.SetStateAction<any>>;
	formErrors: DetailsFormErrors;
	setFormErrors: React.Dispatch<React.SetStateAction<any>>;
	enabledTCstatus: boolean;
	setEnabledTCstatus: React.Dispatch<React.SetStateAction<boolean>>;
}

function DetailsForm({
	formData,
	setFormData,
	formErrors,
	setFormErrors,
	enabledTCstatus,
	setEnabledTCstatus,
}: FormDataProps) {
	const { selectedIndex } = useFormStateStore();
	// const [enabledTC, setEnabledTC] = useState(enabledTCstatus);
	const headingRef = useRef<HTMLHeadingElement>(null);

	// Phone Data
	const phoneFormData = useMemo(
		() => ({
			phone: formData.phone,
			phoneVerified: formData.phoneVerified,
			phoneStage: formData.phoneStage,
		}),
		[formData.phone, formData.phoneVerified, formData.phoneStage]
	);
	const phoneFormErrors = useMemo(
		() => ({
			phone: formErrors.phone,
			phoneVerified: formErrors.phoneVerified,
		}),
		[formErrors.phone, formErrors.phoneVerified]
	);

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;
		let isValid: boolean;
		let sanitized: string;

		if (name === "email") {
			({ isValid, sanitized } = secureInput.email(value));
		} else if (name === "postcode") {
			({ isValid, sanitized } = secureInput.postcode(value));
		} else if (name === "message") {
			({ isValid, sanitized } = secureInput.textarea(value));
		} else {
			({ isValid, sanitized } = secureInput.name(value));
		}

		setFormData((prevData: any) => ({ ...prevData, [name]: sanitized }));
		setFormErrors((prevErrors: any) => ({ ...prevErrors, [name]: !isValid }));
	};

	useEffect(() => {
		setFormData((prevData: any) => ({ ...prevData, tc: enabledTCstatus }));

		if (formErrors.tc) {
			setFormErrors((prevErrors: any) => ({ ...prevErrors, tc: !enabledTCstatus }));
		}
	}, [enabledTCstatus]);

	return (
		<div className="jF-flex jF-flex-col jF-space-y-2">
			<FormHeader
				icon="user"
				title="Your Details"
				description="Tell us a bit about yourself."
				headingRef={headingRef}
			/>
			<div className="jF-flex jF-flex-col jF-space-y-2">
				{/* Row 1: First Name and Last Name */}
				<div className="jF-flex jF-flex-col jF-mt-4 jF-space-y-2 sm:jF-flex-row sm:jF-gap-4 sm:jF-space-y-0">
					{/* First Name */}
					<div className="jF-flex-1 jF-flex jF-flex-col jF-space-y-2">
						<label htmlFor="firstName" className="jF-label">
							First Name
						</label>
						<input
							id="firstName"
							name="firstName"
							aria-label="First Name"
							value={formData.firstName}
							onChange={(e) => {
								handleChange(e);
							}}
							autoComplete="true"
							placeholder="First Name"
							maxLength={50}
							inputMode="text"
							className={clsx(
								formErrors.firstName ? "jF-input-error" : "jF-input-focus",
								"jF-input-default"
							)}
						/>
						{formErrors.firstName && (
							<p className="jF-error-text">First Name is required</p>
						)}
					</div>

					{/* Last Name */}
					<div className="jF-flex-1 jF-flex jF-flex-col jF-space-y-2">
						<label htmlFor="lastName" className="jF-label">
							Last Name
						</label>
						<input
							id="lastName"
							name="lastName"
							value={formData.lastName}
							onChange={(e) => handleChange(e)}
							autoComplete="true"
							placeholder="Last Name"
							maxLength={50}
							inputMode="text"
							className={clsx(
								formErrors.lastName ? "jF-input-error" : "jF-input-focus",
								"jF-input-default"
							)}
						/>
						{formErrors.lastName && (
							<p className="jF-error-text">Last Name is required</p>
						)}
					</div>
				</div>
			</div>

			{/* Row 2: Email and Phone */}
			<div className="jF-flex jF-flex-col jF-space-y-4 sm:jF-flex-row sm:jF-gap-4 sm:jF-space-y-0">
				{/* Email */}
				<div className="jF-flex-1 jF-flex jF-flex-col jF-space-y-2">
					<label htmlFor="email" className="jF-label">
						Email <span className="jF-italic">(optional)</span>
					</label>
					<div className="jF-relative">
						<div className="jF-pointer-events-none jF-absolute jF-inset-y-0 jF-left-0 jF-flex jF-items-center jF-pl-3">
							<EnvelopeIcon
								className="jF-h-5 jF-w-5 jF-text-gray-400"
								aria-hidden="true"
							/>
						</div>
						<input
							value={formData.email}
							onChange={(e) => {
								handleChange(e);
							}}
							aria-label="Email Address"
							type="email"
							id="email"
							name="email"
							placeholder="Email Address"
							maxLength={50}
							autoComplete="true"
							className={clsx(
								formErrors.email && formData.email.length > 0
									? "jF-input-error"
									: "jF-input-focus",
								"jF-input-default jF-pl-10"
							)}
						/>
					</div>
					{formErrors.email && formData.email.length > 0 && (
						<p className="jF-error-text">Enter a valid email</p>
					)}
				</div>
				{/* Phone */}
				<div className="jF-flex-1 jF-flex jF-flex-col">
					<PhoneValidation
						formData={phoneFormData}
						setFormData={setFormData}
						formErrors={phoneFormErrors}
						setFormErrors={setFormErrors}
					/>
				</div>
			</div>

			{/* Row 3: State and Postcode */}
			<div className="jF-flex jF-flex-col jF-space-y-4 sm:jF-flex-row sm:jF-gap-4 sm:jF-space-y-0">
				{/* State */}
				<div className="jF-flex-1 jF-flex jF-flex-col jF-space-y-2">
					<label htmlFor="state" className="jF-label">
						State
					</label>
					<select
						value={formData.state}
						onChange={(e) => {
							handleChange(e);
						}}
						id="state"
						name="state"
						aria-label="State"
						className={clsx(
							formErrors.state ? "jF-select-error" : "jF-select-focus",
							"jF-select-default"
						)}
					>
						{stateOption.map((stateOptions) => (
							<option key={stateOptions.value} value={stateOptions.value}>
								{stateOptions.option}
							</option>
						))}
					</select>
					{formErrors.state && <p className="jF-error-text">State is required</p>}
				</div>
				{/* Postcode */}
				<div className="jF-flex-1 jF-flex jF-flex-col jF-space-y-2">
					<label htmlFor="postcode" className="jF-label">
						Postcode
					</label>
					<input
						value={formData.postcode}
						onChange={(e) => {
							handleChange(e);
						}}
						type="tel"
						aria-describedby="postcode"
						aria-label="Postcode"
						id="postcode"
						name="postcode"
						inputMode="numeric"
						placeholder="Postcode"
						maxLength={4}
						autoComplete="true"
						className={clsx(
							formErrors.postcode ? "jF-input-error" : "jF-input-focus",
							"jF-input-default"
						)}
					/>
					{formErrors.postcode && (
						<p className="jF-error-text">Enter a valid 4-digit postcode</p>
					)}
				</div>
			</div>

			{/* Row 4: Message */}
			<div className="jF-flex-1 jF-flex jF-flex-col jF-space-y-2">
				<label htmlFor="message" className="jF-label">
					Message
				</label>
				<textarea
					value={formData.message}
					onChange={(e) => {
						handleChange(e);
					}}
					id="message"
					name="message"
					aria-label="Message"
					placeholder="Brief description of your financial goals or questions..."
					className={clsx(
						formErrors.message ? "jF-input-error" : "jF-input-focus",
						"jF-input-default"
					)}
				/>
				{formErrors.message && formData.message.length <= 250 && (
					<p className="jF-error-text">Invalid characters in message</p>
				)}
				{formErrors.message && formData.message.length > 250 && (
					<p className="jF-error-text">Message has exceeded the 250 character limit</p>
				)}
			</div>

			{/* Row 5: Toggle for Newsletter */}
			<div id="tc-toggle" className={"jF-m-auto jF-text-center !jF-mt-4"} tabIndex={-1}>
				<ToggleSwitch label="" enabled={enabledTCstatus} setEnabled={setEnabledTCstatus} />
				{formErrors.tc && (
					<span className="jF-error-text">
						Please agree to the terms and conditions to continue
					</span>
				)}
			</div>

			{/* Row 6: Submit Button */}
			{selectedIndex === 2 ? (
				<></>
			) : (
				<div className="jF-m-auto !jF-my-4 jF-w-full md:jF-w-auto">
					<button
						type="submit"
						className={clsx(
							"jF-bg-[--jF-theme-color] jF-text-white jF-text-md md:jF-text-base jF-font-medium jF-px-8 jF-py-4 md:jF-py-2 jF-rounded-md jF-w-full md:jF-w-auto jF-transition-all",
							!enabledTCstatus
								? "jF-bg-gray-400 jF-text-gray-500 jF-cursor-not-allowed"
								: "hover:jF-bg-[--jF-hover-color] jF-cursor-pointer"
						)}
					>
						Confirm Consultation
					</button>
				</div>
			)}
		</div>
	);
}

export default memo(DetailsForm);
